import { ISelectOption } from 'components/ui/Form';
import { COUNTRY_BY_CODE } from 'lib/datasets/countries';
import { STATES_AND_TERRITORIES_BY_CODE } from 'lib/datasets/statesAndTerritories';
import {
  prettyPrintCustomCategoryLabel,
  prettyPrintUserRole,
} from 'lib/helpers/prettyPrint';
import { ILocation, UserProfileRole } from 'lib/types/user';
import _ from 'lodash';

/**
 * Static.
 */
export const BUSINESS_DAY_OPTIONS: ISelectOption<number>[] = [
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
];

export const USER_PROFILE_ROLE_OPTIONS: ISelectOption<UserProfileRole>[] =
  _.map(UserProfileRole, (role) => ({
    label: prettyPrintUserRole(role),
    value: role,
  }));

export const LOCATION_STATE_OPTIONS: ISelectOption<string>[] = _.map(
  STATES_AND_TERRITORIES_BY_CODE,
  (state) => ({
    label: state.name,
    value: state.abbr,
  })
);

/**
 * Dynamically generated.
 */
export const getCountryOptions = (
  locations: ILocation[],
  includeAll?: boolean
) => {
  const allOption = { label: 'All Countries', value: 'all' as any };
  const countryOptions = _.chain(locations)
    .uniqBy('country')
    .map(({ country: code }) => {
      const country = COUNTRY_BY_CODE[code];
      return (
        country && {
          label: code === COUNTRY_BY_CODE.USA.code ? code : country.name,
          value: { country: code },
        }
      );
    })
    .compact()
    .sortBy('label')
    .value();
  return includeAll ? [allOption, ...countryOptions] : countryOptions;
};

export const getStateOptions = (
  locations: ILocation[],
  includeAll?: boolean
) => {
  const allOption = { label: 'All States', value: 'all' as any };
  const stateOptions = _.chain(locations)
    .map((l) => {
      const state = STATES_AND_TERRITORIES_BY_CODE[l.state];
      return state && { label: state.name, value: l };
    })
    .compact()
    .sortBy('label')
    .value();
  return includeAll ? [allOption, ...stateOptions] : stateOptions;
};

export const NO_CUSTOM_CATEGORY_VALUE = '';

export const getCustomCategoryValueLabel = (
  customCategoryLabel: string,
  customCategoryValue: string
) => {
  switch (customCategoryValue) {
    case NO_CUSTOM_CATEGORY_VALUE:
      return `(no ${_.lowerCase(
        prettyPrintCustomCategoryLabel(customCategoryLabel)
      )})`;
    default:
      return customCategoryValue;
  }
};

export const getCustomCategoryValueOptions = (
  customCategoryLabel: string,
  customCategoryValues: string[]
) =>
  _.chain(customCategoryValues)
    .sortBy((c) => c.toLowerCase())
    .map((c) => ({
      label: getCustomCategoryValueLabel(customCategoryLabel, c),
      value: c,
    }))
    .value();

export const NO_DEPARTMENT_VALUE = '';
export const NO_DEPARTMENT_LABEL = '(no department)';

export const getDepartmentLabel = (department: string) => {
  switch (department) {
    case NO_DEPARTMENT_VALUE:
      return NO_DEPARTMENT_LABEL;
    default:
      return department;
  }
};

export const getDepartmentOptions = (departments: string[]) =>
  _.chain(departments)
    .sortBy((d) => d.toLowerCase())
    .map((d) => ({
      label: getDepartmentLabel(d),
      value: d,
    }))
    .value();

export const NO_EMPLOYEE_TYPE_VALUE = '';
export const NO_EMPLOYEE_TYPE_LABEL = '(no worker type)';

export const getEmployeeTypeLabel = (employeeType: string) => {
  switch (employeeType) {
    case NO_EMPLOYEE_TYPE_VALUE:
      return NO_EMPLOYEE_TYPE_LABEL;
    default:
      return employeeType;
  }
};

export const getEmployeeTypeOptions = (employeeTypes: string[]) =>
  _.chain(employeeTypes)
    .sortBy((w) => w.toLowerCase())
    .map((w) => ({
      label: getEmployeeTypeLabel(w),
      value: w,
    }))
    .value();
